import type { RecordProxy, RecordSourceSelectorProxy } from 'relay-runtime';

import { type MenuItemReorderOperation, type OrderPreference } from '../../utils/dnd-data';

import { reorderNestedItems } from './async';

export function extractMenuItemsFromNodes(
	nodes: ReadonlyArray<RecordProxy | null> | null | undefined,
): OrderPreference {
	const menuItems: OrderPreference = [];

	if (!nodes) {
		return menuItems;
	}

	nodes.forEach((node) => {
		if (!node) {
			return;
		}

		const menuId = node.getValue('menuId');
		const visible = node.getValue('visible');

		if (typeof menuId === 'string' && typeof visible === 'boolean') {
			menuItems.push({ menuId, visible });
		}
	});

	return menuItems;
}

export function convertToStringId(value: unknown): string | null {
	if (typeof value === 'number') {
		return value.toString();
	}
	if (typeof value === 'string') {
		return value;
	}
	return null;
}

export function getVisibleMenuIds(menuItems: OrderPreference): string[] {
	return menuItems.filter((item) => item.visible === true).map((item) => item.menuId);
}

export function determineBaseOrder(
	existingOrderFromSettings: string[],
	currentOrderedIds: string[],
): string[] {
	if (existingOrderFromSettings.length === 0) {
		return [...currentOrderedIds];
	}

	const baseOrder = existingOrderFromSettings.filter((itemId) =>
		currentOrderedIds.includes(itemId),
	);

	const missingItems = currentOrderedIds.filter(
		(itemId) => !existingOrderFromSettings.includes(itemId),
	);

	return [...baseOrder, ...missingItems];
}

export function createMenuItemsFromOrder(
	reorderedIds: string[],
	originalMenuItems: OrderPreference,
): OrderPreference {
	const newMenuItems: OrderPreference = reorderedIds.map((itemId) => ({
		menuId: itemId,
		visible: true,
	}));

	// Add any items that were in the original settings
	originalMenuItems.forEach((item) => {
		if (!reorderedIds.includes(item.menuId)) {
			newMenuItems.push({
				menuId: item.menuId,
				visible: item.visible,
			});
		}
	});

	return newMenuItems;
}

export function getCurrentOrder(
	store: RecordSourceSelectorProxy,
	entityAri: string,
	ownerAri: string,
): OrderPreference {
	// Access the settings_navigationCustomisation data
	const currentOrder = store.getRoot().getLinkedRecord('settings_navigationCustomisation', {
		entityAri,
		ownerAri,
	});

	// Access the sidebar nodes if the record exists
	const sidebar = currentOrder?.getLinkedRecord('sidebar');
	const nodes = sidebar?.getLinkedRecords('nodes');

	return extractMenuItemsFromNodes(nodes);
}

type CalculateNewSidebarItemOrder<T> = (args: {
	currentItems?: T[];
	entityAri: string;
	getItemId: (item: T) => string;
	operation: MenuItemReorderOperation;
	ownerAri: string;
	relativeItemId?: string;
	store: RecordSourceSelectorProxy;
	targetItemId: string;
}) => OrderPreference;

export const calculateNewSidebarItemOrder: CalculateNewSidebarItemOrder<unknown> = ({
	store,
	entityAri,
	ownerAri,
	operation,
	targetItemId,
	relativeItemId,
	currentItems,
	getItemId,
}) => {
	const currentOrder = store.getRoot().getLinkedRecord('settings_navigationCustomisation', {
		entityAri,
		ownerAri,
	});

	const sidebar = currentOrder?.getLinkedRecord('sidebar');
	const nodes = sidebar?.getLinkedRecords('nodes');
	const originalMenuItems = extractMenuItemsFromNodes(nodes);
	const existingOrderFromSettings = getVisibleMenuIds(originalMenuItems);
	const availableItems = currentItems?.map((item) => getItemId(item)) || [];
	const baseOrder = determineBaseOrder(existingOrderFromSettings, availableItems);
	const newOrder = reorderNestedItems(operation, baseOrder, targetItemId, relativeItemId);
	return createMenuItemsFromOrder(newOrder, originalMenuItems);
};

/**
 * Creates Relay record proxies for menu items
 * Handles the complexity of Relay store record creation
 */
export function createMenuItemRecords(
	store: RecordSourceSelectorProxy,
	menuItems: OrderPreference,
	entityAri: string,
): RecordProxy[] {
	return menuItems.map((nodeData) => {
		const nodeRecord = store.create(
			`SettingsMenuItem:${entityAri}:${nodeData.menuId}`,
			'SettingsMenuItem',
		);
		nodeRecord.setValue(nodeData.menuId, 'menuId');
		nodeRecord.setValue(nodeData.visible, 'visible');
		return nodeRecord;
	});
}

/**
 * Updates the navigation customisation in the Relay store
 * Handles all the different cases (existing sidebar, no sidebar, no settings)
 */
export function updateNavigationCustomisationInStore(
	store: RecordSourceSelectorProxy,
	entityAri: string,
	ownerAri: string,
	menuItemRecords: RecordProxy[],
): void {
	const currentOrder = store.getRoot().getLinkedRecord('settings_navigationCustomisation', {
		entityAri,
		ownerAri,
	});

	const sidebar = currentOrder?.getLinkedRecord('sidebar');

	if (sidebar) {
		// Update existing sidebar
		sidebar.setLinkedRecords(menuItemRecords, 'nodes');
	} else if (currentOrder) {
		// Create sidebar for existing currentOrder
		const newSidebar = store.create(
			`SettingsMenuItemConnection:${entityAri}`,
			'SettingsMenuItemConnection',
		);
		newSidebar.setLinkedRecords(menuItemRecords, 'nodes');
		currentOrder.setLinkedRecord(newSidebar, 'sidebar');
	} else {
		// Create entire structure from scratch
		const newOrderRecord = store.create(
			`SettingsNavigationCustomisation:${entityAri}:${ownerAri}`,
			'SettingsNavigationCustomisation',
		);
		const newSidebar = store.create(
			`SettingsMenuItemConnection:${entityAri}`,
			'SettingsMenuItemConnection',
		);
		newSidebar.setLinkedRecords(menuItemRecords, 'nodes');
		newOrderRecord.setLinkedRecord(newSidebar, 'sidebar');

		store.getRoot().setLinkedRecord(newOrderRecord, 'settings_navigationCustomisation', {
			entityAri,
			ownerAri,
		});
	}
}

type OptimisticNestedItemUpdater<T> = (args: {
	currentItems?: T[];
	entityAri: string;
	getItemId: (item: T) => string;
	operation: MenuItemReorderOperation;
	ownerAri: string;
	relativeItemId?: string;
	store: RecordSourceSelectorProxy;
	targetItemId: string;
}) => void;

export const optimisticNestedItemUpdater: OptimisticNestedItemUpdater<unknown> = ({
	store,
	operation,
	entityAri,
	ownerAri,
	targetItemId,
	relativeItemId,
	currentItems,
	getItemId,
}) => {
	// Calculate the new order using the pure function
	const newMenuItems = calculateNewSidebarItemOrder({
		store,
		entityAri,
		ownerAri,
		operation,
		targetItemId,
		relativeItemId,
		currentItems,
		getItemId,
	});

	// Update the store if we have menu items
	if (newMenuItems.length > 0) {
		const menuItemRecords = createMenuItemRecords(store, newMenuItems, entityAri);
		updateNavigationCustomisationInStore(store, entityAri, ownerAri, menuItemRecords);
	}
};
